// „Jak się poruszać" (SPEC-redesign §5.9). Six glyph/explanation pairs, verbatim
// from the prototype, and „Pokaż jeszcze raz" pinned to the bottom. The seventh
// pair is the keyboard, and it exists only at the desktop breakpoint (D-e, §7.1):
// there is no key to press on a phone, and a line about Shift+Enter under a
// thumb would be the kind of copy this theme exists to remove.
import { viewState } from '../state/viewstate.ts';
import { replayIntro } from './intro.ts';

export type HelpLine = { glyph: string; heading: string; body: string };

/**
 * The six, in the prototype's order. `desktopOnly` is the seventh and is
 * rendered behind the 900 px breakpoint by `screens.css`, not by a device test:
 * a CSS breakpoint is the ruling (§5.10, ruling 12), so the line is always in
 * the DOM and the stylesheet decides whether it is shown.
 */
export const HELP_LINES: readonly HelpLine[] = [
  {
    glyph: '◌',
    heading: 'Ty jesteś środkiem.',
    body: 'Koło mierzy odległość od Ciebie, oś pod nim liczy czas od „teraz”. Kreska między nimi to jedno i drugie.',
  },
  {
    glyph: '⇅',
    heading: 'Przeciągnij kreskę.',
    body: 'W dół: większe koło. W górę: więcej osi, koło zmienia się w tarczę.',
  },
  {
    glyph: '⟷',
    heading: 'Zmień promień.',
    body: 'Suwak pod kołem, kółko myszy albo dwa palce na mapie — od 200 m do 5 km. Pierścienie liczą się same.',
  },
  {
    glyph: '◇',
    heading: 'Przenieś środek.',
    body: 'Przesuń mapę albo wybierz „Stąd” w szczególe; „Stąd, o 15:00” przenosi też w czasie. Celownik przy nazwie wraca do „ja, teraz”.',
  },
  {
    glyph: 'ZTM',
    heading: 'Wyciszaj głosy.',
    body: 'Dotknij znak źródła w wierszu: cisza. Przytrzymaj: tylko ten głos. Wyciszone wiersze zostają jako cienkie linie.',
  },
  {
    glyph: '‹',
    heading: 'Wstecz zamyka.',
    body: 'Każdy szczegół, ekran i przeniesienie środka cofa się gestem wstecz albo klawiszem Esc. Adres strony pamięta wszystko.',
  },
];

/**
 * The seventh line (§7.1), in the DOM always and on screen only past 900 px.
 *
 * The map clause OPENS it, which is the owner's own choice of the two he was
 * offered (approved 2026-09-21, SPEC-viewfinder §5): the clause is about the
 * map and the map is above the rail on the screen. It names no fraction — a
 * resident does not press a key to travel an eighth of the radius — and it does
 * not mention the tab stop, because anyone reading this card arrived by
 * keyboard already.
 */
export const KEYBOARD_LINE: HelpLine = {
  glyph: '⏎',
  heading: 'Klawiatura.',
  body: 'Strzałki przesuwają środek mapy, z Shiftem dalej. Enter na znaku źródła wycisza. Shift+Enter zostawia tylko ten głos. Esc cofa o krok.',
};

function lineEl(line: HelpLine, desktopOnly = false): HTMLElement {
  const row = document.createElement('div');
  row.className = desktopOnly ? 'help__line help__line--keyboard' : 'help__line';

  const glyph = document.createElement('span');
  glyph.className = 'help__glyph';
  glyph.textContent = line.glyph;
  // the glyph repeats what the heading already says, so a screen reader that
  // reads both would read the gesture twice
  glyph.setAttribute('aria-hidden', 'true');

  const words = document.createElement('p');
  words.className = 'help__words';
  const heading = document.createElement('strong');
  heading.textContent = line.heading;
  // Its own element rather than a bare text node beside the heading: the copy
  // is the owner's and a test has to be able to point at exactly it.
  const body = document.createElement('span');
  body.className = 'help__body';
  body.textContent = line.body;
  words.append(heading, body);

  row.append(glyph, words);
  return row;
}

/** The screen's body. The `‹ Jak się poruszać` header belongs to the overlay host. */
export function helpScreen(): HTMLElement {
  const body = document.createElement('div');
  body.className = 'help';

  for (const line of HELP_LINES) body.append(lineEl(line));
  body.append(lineEl(KEYBOARD_LINE, true));

  const again = document.createElement('button');
  again.type = 'button';
  again.className = 'help__again';
  again.textContent = 'Pokaż jeszcze raz';
  // As in the prototype: it closes the screen and replays the reveal from the
  // top. The close goes through `closeScreen()`, so it leaves no entry Back
  // could re-open the help from (§4.2, D-1); the replay follows it.
  again.addEventListener('click', () => {
    viewState.closeScreen();
    replayIntro();
  });
  body.append(again);

  return body;
}
